import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Building, Calendar, Car, DollarSign, FileText, TrendingUp, 
  Download, Filter, ArrowLeft, Clock, CheckCircle, XCircle,
  BarChart3, PieChart, CalendarRange
} from "lucide-react";

const CorporatePortal = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Data state
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [spendingReport, setSpendingReport] = useState(null);
  
  // Filter state
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [reportPeriod, setReportPeriod] = useState("month");

  useEffect(() => {
    if (!user?.company_name) {
      toast.error("This portal is for corporate accounts only");
      navigate("/client");
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bookingsRes, invoicesRes, reportRes] = await Promise.all([
        api.get("/corporate/my-bookings"),
        api.get("/corporate/my-invoices"),
        api.get(`/corporate/spending-report?period=${reportPeriod}`)
      ]);
      setBookings(bookingsRes.data);
      setInvoices(invoicesRes.data);
      setSpendingReport(reportRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredBookings = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("start_date", dateFrom);
      if (dateTo) params.append("end_date", dateTo);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      
      const response = await api.get(`/corporate/my-bookings?${params.toString()}`);
      setBookings(response.data);
    } catch (error) {
      toast.error("Failed to filter bookings");
    }
  };

  const fetchReport = async (period) => {
    setReportPeriod(period);
    try {
      const response = await api.get(`/corporate/spending-report?period=${period}`);
      setSpendingReport(response.data);
    } catch (error) {
      toast.error("Failed to load report");
    }
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setStatusFilter("");
    fetchData();
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: "bg-yellow-500/20 text-yellow-500",
      confirmed: "bg-blue-500/20 text-blue-500",
      dispatched: "bg-purple-500/20 text-purple-500",
      completed: "bg-green-500/20 text-green-500",
      cancelled: "bg-red-500/20 text-red-500"
    };
    return <Badge className={variants[status] || ""}>{status}</Badge>;
  };

  const getInvoiceStatusBadge = (status) => {
    const variants = {
      draft: "bg-gray-500/20 text-gray-500",
      sent: "bg-blue-500/20 text-blue-500",
      paid: "bg-green-500/20 text-green-500",
      overdue: "bg-red-500/20 text-red-500"
    };
    return <Badge className={variants[status] || ""}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Car className="h-12 w-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-background/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Building className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-lg font-semibold">{user?.company_name}</h1>
              <p className="text-xs text-muted-foreground">Corporate Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {spendingReport?.discount_percentage > 0 && (
              <Badge className="bg-green-500/20 text-green-500">
                {spendingReport.discount_percentage}% Discount
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/login"); }}>
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-secondary/50 backdrop-blur-sm border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: "overview", label: "Overview", icon: BarChart3 },
              { id: "bookings", label: "Bookings", icon: Calendar },
              { id: "invoices", label: "Invoices", icon: FileText },
              { id: "reports", label: "Spending Report", icon: PieChart }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id 
                    ? "text-primary border-b-2 border-primary" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="pt-32 px-4 pb-8 max-w-7xl mx-auto">
        {/* Overview Tab */}
        {activeTab === "overview" && spendingReport && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Welcome, {user?.name}</h2>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="dashboard-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                      <Calendar className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Bookings</p>
                      <p className="text-2xl font-bold">{spendingReport.summary.total_bookings}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="dashboard-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <DollarSign className="h-6 w-6 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Spent</p>
                      <p className="text-2xl font-bold">${spendingReport.summary.total_spent.toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="dashboard-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Discount Saved</p>
                      <p className="text-2xl font-bold text-green-500">${spendingReport.summary.total_discount_saved.toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="dashboard-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
                      <FileText className="h-6 w-6 text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Unpaid Invoices</p>
                      <p className="text-2xl font-bold">${spendingReport.invoices.unpaid_total.toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Bookings */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle>Recent Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {bookings.slice(0, 5).map(booking => (
                    <div key={booking.id} className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
                      <div>
                        <p className="font-medium">{booking.booking_ref}</p>
                        <p className="text-sm text-muted-foreground">{booking.passenger_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm">{new Date(booking.pickup_datetime).toLocaleDateString()}</p>
                        {getStatusBadge(booking.status)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Your Bookings</h2>
            
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-secondary/30 border border-white/5">
              <div className="flex items-center gap-2">
                <CalendarRange className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Filter:</span>
              </div>
              <div className="flex-1">
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="bg-secondary/50 border-black/10"
                  placeholder="From"
                />
              </div>
              <div className="flex-1">
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="bg-secondary/50 border-black/10"
                  placeholder="To"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 bg-secondary/50 border-black/10">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={fetchFilteredBookings}>
                <Filter className="h-4 w-4 mr-2" />
                Apply
              </Button>
              <Button variant="outline" onClick={clearFilters}>Clear</Button>
            </div>

            {/* Bookings Table */}
            <Card className="dashboard-card">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Your Ref</th>
                        <th>Passenger</th>
                        <th>Route</th>
                        <th>Date</th>
                        <th>Price</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map(booking => (
                        <tr key={booking.id}>
                          <td className="mono text-primary">{booking.booking_ref}</td>
                          <td className="text-amber-500">{booking.corporate_reference || "-"}</td>
                          <td>{booking.passenger_name}</td>
                          <td>
                            <div className="max-w-[200px]">
                              <p className="text-sm truncate">{booking.pickup_location}</p>
                              <p className="text-xs text-muted-foreground truncate">→ {booking.dropoff_location}</p>
                            </div>
                          </td>
                          <td>{new Date(booking.pickup_datetime).toLocaleDateString()}</td>
                          <td>
                            {booking.discount_amount > 0 ? (
                              <div>
                                <span className="line-through text-muted-foreground text-xs">${booking.original_price?.toFixed(2)}</span>
                                <span className="ml-2 text-green-500">${booking.price.toFixed(2)}</span>
                              </div>
                            ) : (
                              `$${booking.price.toFixed(2)}`
                            )}
                          </td>
                          <td>{getStatusBadge(booking.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Your Invoices</h2>
            
            <Card className="dashboard-card">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Issue Date</th>
                        <th>Due Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map(invoice => (
                        <tr key={invoice.id}>
                          <td className="mono text-primary">{invoice.invoice_number}</td>
                          <td>{invoice.issued_date?.substring(0, 10)}</td>
                          <td>{invoice.due_date?.substring(0, 10)}</td>
                          <td className="font-bold">${invoice.total?.toFixed(2)}</td>
                          <td>{getInvoiceStatusBadge(invoice.status)}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoice.id}/pdf`, '_blank')}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              PDF
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === "reports" && spendingReport && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Spending Report</h2>
              <Select value={reportPeriod} onValueChange={fetchReport}>
                <SelectTrigger className="w-40 bg-secondary/50 border-black/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="quarter">Last 90 Days</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="dashboard-card">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-primary">{spendingReport.summary.total_bookings}</p>
                  <p className="text-muted-foreground">Total Bookings</p>
                </CardContent>
              </Card>
              <Card className="dashboard-card">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold">${spendingReport.summary.total_spent.toFixed(2)}</p>
                  <p className="text-muted-foreground">Total Spent</p>
                </CardContent>
              </Card>
              <Card className="dashboard-card bg-green-500/10">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-green-500">${spendingReport.summary.total_discount_saved.toFixed(2)}</p>
                  <p className="text-muted-foreground">Saved with {spendingReport.discount_percentage}% Discount</p>
                </CardContent>
              </Card>
            </div>

            {/* By Vehicle Type */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle>Spending by Vehicle Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(spendingReport.by_vehicle_type).map(([type, data]) => (
                    <div key={type} className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
                      <div className="flex items-center gap-3">
                        <Car className="h-5 w-5 text-primary" />
                        <span className="font-medium capitalize">{type}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">${data.total.toFixed(2)}</p>
                        <p className="text-sm text-muted-foreground">{data.count} bookings</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Monthly Breakdown */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle>Monthly Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(spendingReport.monthly_breakdown).slice(-6).map(([month, data]) => (
                    <div key={month} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                      <span className="font-medium">{month}</span>
                      <div className="text-right">
                        <p className="font-bold">${data.total.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">{data.count} bookings</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default CorporatePortal;
